/**
 * Normalisation of persisted card rows (`cards_v2` / Dexie `cards`) into a
 * store bucket — the ONE path shared by hydrate's stage 1 and the per-deck
 * disk loader (`internal/deck-disk-load.ts`).
 *
 * Both readers MUST produce byte-identical rows for the same disk content:
 * the dirty scan's heal-on-touch stamps `updatedAtISO` when it is missing, so
 * a loader that skipped a heal the other one applied would make every card of
 * the deck look freshly edited and push the whole deck on the next flush.
 *
 * Per row, in this order:
 *   1. drop the persisted `deckId` (the bucket key carries it);
 *   2. `cleanStoredCard` — strip known pollution fields, backfill the
 *      personal-scope arrays, coerce malformed string fields (mirrors
 *      `deck-slice.cleanCardForStore` for the write boundary);
 *   3. heal a missing `updatedAtISO` with the card's `createdTimeISO` (a
 *      STABLE, past value — never now(), which would out-rank the server's
 *      last-synced ts and re-push the card every sync);
 *   4. `backfillSortKeys` over the deck (only the missing keys; existing keys
 *      and refs preserved).
 *
 * `repaired` holds the rows whose reference changed in steps 3–4 — the ones
 * worth writing back so the repair sticks across loads. A row changed by step
 * 2 alone is deliberately NOT reported: the cleaning is idempotent and cheap,
 * and writing it back would re-persist every legacy row on every boot.
 */
import type { SyncCardProps as CardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import type { StoredCard, SyncStorage } from '../../provider/sync-storage';
import { backfillSortKeys } from '../utils/sort-key';
import { hasMalformedStringField, stripMalformedStringFields } from './card-sanitize';

// Defensive fallback for healing a legacy card with neither `updatedAtISO`
// nor `createdTimeISO`. The epoch is intentionally ancient so the healed
// value is always ≤ the server's last-synced timestamp (never re-pushed).
export const REPAIR_FALLBACK_ISO = new Date(0).toISOString();

/**
 * Known-bad fields that leak into card objects via UI/routing spreads and
 * must not reach the store — the historical offender is `deckName` (see
 * `deck-slice.ts` for the full account). Heals rows that earlier SG-mirror
 * seeds wrote with projected (personal-stripped) cards.
 */
const STRIPPED_KEYS: ReadonlySet<string> = new Set(['deckName']);

/** Same scrub as `deck-slice.cleanCardForStore`, for rows read from disk. */
export function cleanStoredCard(card: CardProps): CardProps {
  let needsStrip = false;
  for (const key of Object.keys(card)) {
    if (STRIPPED_KEYS.has(key)) {
      needsStrip = true;
      break;
    }
  }
  // Anything but an array — `undefined` from an old row, but also `null` from
  // a server diff or a mirrored row — is repaired to empty: every reader does
  // `.length` on these, and one launch-time throw repeats on every relaunch.
  const needsActionsBackfill = !Array.isArray(card.actions);
  const needsStudyStateBackfill = !Array.isArray(card.studyState);
  const needsStringCoerce = hasMalformedStringField(card as Record<string, unknown>);
  if (
    !needsStrip &&
    !needsActionsBackfill &&
    !needsStudyStateBackfill &&
    !needsStringCoerce
  )
    return card;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(card)) {
    if (!STRIPPED_KEYS.has(key)) {
      out[key] = (card as Record<string, unknown>)[key];
    }
  }
  if (needsActionsBackfill) out.actions = [];
  if (needsStudyStateBackfill) out.studyState = [];
  if (needsStringCoerce) stripMalformedStringFields(out);
  return out as CardProps;
}

export type NormalizedDeckCardRows = {
  /** The deck's bucket, keyed by card id. */
  map: Record<string, CardProps>;
  /** Rows healed or re-keyed — persist them so the repair sticks. */
  repaired: StoredCard[];
};

/**
 * Normalise ONE deck's persisted rows into its bucket. `rows` must all belong
 * to `deckId`; the persisted `deckId` field is dropped from every row.
 */
export function normalizeDeckCardRows(
  deckId: string,
  rows: readonly StoredCard[],
): NormalizedDeckCardRows {
  const cleaned: CardProps[] = [];
  for (const row of rows) {
    const { deckId: _dropped, ...rest } = row;
    cleaned.push(cleanStoredCard(rest as CardProps));
  }
  const healed = cleaned.map((c) =>
    c.updatedAtISO ? c : { ...c, updatedAtISO: c.createdTimeISO || REPAIR_FALLBACK_ISO },
  );
  const filled = backfillSortKeys(healed);
  const map: Record<string, CardProps> = {};
  const repaired: StoredCard[] = [];
  for (let i = 0; i < filled.length; i++) {
    const c = filled[i];
    map[c.id] = c;
    // Reference changed ⇒ this row was healed and/or re-keyed.
    if (c !== cleaned[i]) repaired.push({ ...c, deckId } as StoredCard);
  }
  return { map, repaired };
}

/**
 * Read ONE deck's rows from local storage and normalise them. The read is the
 * PK-prefix query `getByIndex('cards', 'deckId', id)` (`cards_v2` is keyed
 * `(deck_id, id)`; Dexie indexes `deckId`), which awaits the storage flush
 * first — so it sees every row a frame or edit has already queued.
 */
export async function readDeckCardRowsFromDisk(
  storage: SyncStorage,
  deckId: string,
): Promise<NormalizedDeckCardRows> {
  const rows = await storage.getByIndex<StoredCard>('cards', 'deckId', deckId);
  return normalizeDeckCardRows(deckId, rows);
}

/**
 * Write repaired rows back so the repair sticks across loads instead of being
 * recomputed (and, before the heal, re-pushed) on every read. Best-effort:
 * the rows are already correct in memory. Resolves once durable.
 */
export async function persistRepairedRows(
  storage: SyncStorage,
  rows: readonly StoredCard[],
): Promise<void> {
  if (rows.length === 0) return;
  if (storage.bulkPut) {
    await storage.bulkPut('cards', rows as StoredCard[]);
    return;
  }
  for (const row of rows) storage.put('cards', row);
  await storage.flush?.();
}
